import { useQueryClient } from '@tanstack/react-query';
import { useState, useId } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { fmt, pct } from '../lib/format';
import { ActionButton, ErrorBox, KV, Load, Page, Section, Status, Table, useApi } from '../ui/kit';

// F05 onboarding: upload a CRM export, confirm the column mapping, see what is missing; then the win/loss analysis.

const FIELDS = ['domain', 'name', 'country', 'employees', 'industry', 'deal_id', 'stage', 'source', 'amount', 'created_date', 'close_date', 'outcome', 'loss_reason', 'competitor'];

export function Imports() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<{ id: string; headers: string[]; suggested_mapping: Record<string, string>; personal_columns: string[]; sample: string[][]; warnings: string[] } | null>(null);
  const upload = async (file: File) => {
    setErr(null); setBusy(true);
    try {
      const c = await api<{ import_id: string; upload_url: string }>('/v1/imports', { method: 'POST', body: { filename: file.name } });
      const put = await fetch(c.upload_url, { method: 'PUT', body: file, headers: { 'content-type': 'text/csv' } });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      const inspected = await api(`/v1/imports/${c.import_id}/uploaded`, { method: 'POST' });
      setCurrent({ id: c.import_id, ...inspected });
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const confirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null);
    const f = new FormData(e.currentTarget);
    const mapping = Object.fromEntries(FIELDS.map((k) => [k, String(f.get(k) ?? '')]).filter(([, v]) => v));
    try {
      await api(`/v1/imports/${current!.id}/mapping`, { method: 'PUT', body: { mapping, save_as: f.get('save_as') || undefined,
        contacts: f.get('contacts') === 'on' ? { enabled: true, purpose: String(f.get('purpose') ?? '') } : undefined } });
      setCurrent(null); await qc.invalidateQueries();
    } catch (x) { setErr(x); }
  };
  return (
    <Page title="Import accounts and deals">
      <Section title="Upload a CSV" actions={<label className="btn btn-primary">Choose file<input id={`${uid}-f1`} type="file" accept=".csv,text/csv" className="sr-only" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} /></label>}>
        <p className="muted small">Up to 50 MB or 200,000 rows. Analysis runs per company; ten deals at one company count as one example. Columns about people are dropped unless you enable contacts with a stated purpose; kept, each person with an address at the company's domain becomes a contact, checked for eligibility before anything is sent.</p>
        {busy ? <p role="status">Uploading and reading the header…</p> : null}
        {err ? <ErrorBox error={err} /> : null}
      </Section>
      {current ? (
        <Section title="Confirm the column mapping">
          {current.warnings.map((w) => <p key={w} className="error" role="alert">{w}</p>)}
          <form className="form" onSubmit={confirm}>
            <div className="grid">{FIELDS.map((k) => (
              <div className="field" key={k}><label htmlFor={`m-${k}`}>{k.replace(/_/g, ' ')}</label>
                <select id={`m-${k}`} name={k} defaultValue={current.suggested_mapping[k] ?? ''}><option value="">(not in file)</option>{current.headers.map((h) => <option key={h} value={h}>{h}</option>)}</select></div>
            ))}</div>
            {current.personal_columns.length ? <div className="field field-check"><input id="contacts" name="contacts" type="checkbox" /><label htmlFor="contacts">Keep contact columns ({current.personal_columns.join(', ')})</label></div> : null}
            {current.personal_columns.length ? <div className="field"><label htmlFor="purpose">Purpose (required to keep contacts)</label><input id="purpose" name="purpose" /></div> : null}
            <div className="field"><label htmlFor="save_as">Save this mapping as (optional)</label><input id="save_as" name="save_as" /></div>
            <Table rows={current.sample.map((r) => Object.fromEntries(current.headers.map((h, i) => [h, r[i]])))} caption="Sample rows" />
            <div className="form-actions"><button type="submit" className="btn btn-primary">Import</button></div>
          </form>
        </Section>
      ) : null}
      <Load<{ data: any[] }> path="/v1/integrations">{(d: any) => d.connections?.length ? <Section title="From your CRM">{d.connections.map((c: any) => <ActionButton key={c.id} path={`/portal/imports/crm/${c.id}`} label={`Import from ${c.provider}`} confirm="Bring the CRM's companies and deals in?" />)}</Section> : null}</Load>
      <p><Link to="/import/duplicates">Companies on the same domain</Link> <span className="muted small">— merge them into one</span></p>
      <Load<{ data: any[] }> path="/v1/imports">{(d) => <Section title="Imports"><Table rows={d.data} rowLink={(r) => `/import/${r.id}`} columns={['filename', 'source', 'status', 'row_count', 'companies', 'deals', 'error_count', 'created_at']} empty="Nothing imported yet." /></Section>}</Load>
      <Analysis />
    </Page>
  );
}

export function ImportDetail() {
  const { id } = useParams();
  const q = useApi<any>(`/v1/imports/${id}`, { refetchMs: 3000 });
  if (!q.data) return <Page title="Import">{q.error ? <ErrorBox error={q.error} /> : <p role="status">Loading…</p>}</Page>;
  const i = q.data;
  return (
    <Page title={i.filename ?? `${i.source} import`} actions={<Status value={i.status} />}>
      {i.warnings?.map((w: string) => <p key={w} className="banner">{w}{/registrable domain/.test(w) ? <> <Link to="/import/duplicates">Review and merge</Link></> : null}</p>)}
      {i.quality?.dedupe ? <Section title="What it did to Accounts"><p><strong>{i.quality.dedupe.summary}</strong>{i.quality.dedupe.possible_duplicates ? <> · <Link to="/import/duplicates">Review possible duplicates</Link></> : null}</p>
        {i.quality.dedupe.aliases?.length ? <p className="muted small">Landed on an account you already have: {i.quality.dedupe.aliases.map((a: { from: string; to: string }) => `${a.from} → ${a.to}`).join(', ')}</p> : null}</Section> : null}
      {i.quality?.contacts ? <Section title="Contacts"><KV data={{ new: i.quality.contacts.stored, already_in_contacts: i.quality.contacts.known, gaps_filled: i.quality.contacts.filled ?? 0, eligible: i.quality.contacts.eligible, dropped: fmt(i.quality.contacts.dropped) }} /></Section> : null}
      <Section title="Summary"><KV data={{ rows: i.row_count, companies: i.companies, deals: i.deals, rejected: i.error_count, finished_at: i.finished_at, error: typeof i.error === 'object' && i.error ? i.error.message ?? i.error.code : i.error }} /></Section>
      {i.quality ? <Section title="Data quality"><Table rows={i.quality.fields} columns={['field', { key: 'fill_rate', render: (r) => pct(r.fill_rate) }, { key: 'issues', render: (r) => fmt(r.issues) }]} /></Section> : null}
      {i.rejected?.length ? <Section title="Rejected rows (sample)"><Table rows={i.rejected} columns={['row', 'reason']} /></Section> : null}
      <Link to="/import">Back to imports</Link>
    </Page>
  );
}

/** Same words as src/imports/analysis-review.ts ANALYSIS_UNVERIFIED and ANALYSIS_REVIEWING (tests/analysis-review.test.ts). */
export const ANALYSIS_UNVERIFIED = 'The analysis couldn\'t be verified: a number in its summary was not in the table, so the summary is not shown. Here\'s the table; you can run the analysis again.';
export const ANALYSIS_REVIEWING = 'A number in the summary was not in the table, so Outlevo\'s AI reviewer is checking the summary against the table before anyone reads it.';

function Analysis() {
  return (
    <Load<{ data: any[] }> path="/v1/analysis-runs">{(d) => (
      <Section title="Win/loss analysis" actions={<ActionButton path="/v1/analysis-runs" label="Run analysis" variant="primary" />}>
        <p className="muted small">Counts with denominators. Under 10 won companies the analysis lists data-quality findings and questions only; segments never tried read as untested.</p>
        <Table rows={d.data} columns={['status', 'tier', 'created_at', 'finished_at', { key: 'id', label: '', render: (r) => (r.status === 'succeeded' || r.status === 'unverified' ? <RunView id={r.id} /> : r.status === 'review' ? <span className="small">{ANALYSIS_REVIEWING}</span> : null) }]} empty="No analysis yet." />
      </Section>
    )}</Load>
  );
}

function RunView({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const q = useApi<any>(open ? `/v1/analysis-runs/${id}` : null);
  return (
    <div>
      <button type="button" className="btn" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>
      {open && q.data ? <div style={{ marginTop: 8 }}>
        {q.data.status === 'unverified' ? <p role="status">{ANALYSIS_UNVERIFIED}</p> : <p>{q.data.narrative}</p>}
        {q.data.questions?.length ? <><strong>Questions</strong><ul>{q.data.questions.map((x: string) => <li key={x}>{x}</li>)}</ul></> : null}
        <Table rows={q.data.results?.comparisons ?? []} columns={['row', 'attribute', 'value', 'won_companies', 'won_denominator', 'lost_companies', 'lost_denominator', 'reading']} empty="No comparison at this tier." />
        {q.data.icp_version_id ? <Link to={`/icp/versions/${q.data.icp_version_id}`}>Hypotheses were added to your ICP draft</Link> : null}
      </div> : null}
    </div>
  );
}

type DupCompany = { id: string; domain: string; name: string | null; relationship: string; status: string | null; contacts: number; deals: number; actions: number; created_at: string };
type DupGroup = { registrable: string; suggested_survivor_id: string; companies: DupCompany[] };

/** F05 import.dedupe: companies on one registrable domain; the person picks the one that stays. */
export function ImportDuplicates() {
  return (
    <Page title="Companies on the same domain">
      <p className="muted small">These companies share a registrable domain (app.acme.com and acme.com are one company). Pick the one to keep: the others' contacts, research, deals, actions and links move to it, and the merge is recorded in the audit log. It cannot be undone.</p>
      <Load<{ data: DupGroup[] }> path="/v1/companies/duplicates">{(d) => d.data.length
        ? <>{d.data.map((g) => <DuplicateGroupView key={g.registrable} group={g} />)}</>
        : <p>No duplicates: every company has its own registrable domain.</p>}</Load>
      <h2>Possible duplicates</h2>
      <p className="muted small">The same company name on two domains (Acme on acme.com and acme.io). They may be one company or two, so nothing is merged unless you say so. "Not the same" keeps them apart and stops the suggestion.</p>
      <Load<{ data: PossiblePair[] }> path="/v1/companies/possible-duplicates">{(d) => d.data.length
        ? <>{d.data.map((p) => <PossiblePairView key={`${p.a.id}:${p.b.id}`} pair={p} />)}</>
        : <p>No possible duplicates.</p>}</Load>
      <Load<{ data: PossiblePeople[] }> path="/v1/contacts/possible-duplicates">{(d) => d.data.length ? (
        <Section title="People who may be one person">
          <p className="muted small">The same name at one company with two addresses. Keep both if they are two mailboxes of one person you want to reach either way; otherwise mark them as different.</p>
          <Table rows={d.data.map((p) => ({ ...p, id: `${p.a.id}:${p.b.id}` }))} columns={[
            { key: 'name', label: 'Person', render: (r) => [r.a.first_name, r.a.last_name].filter(Boolean).join(' ') },
            { key: 'domain', label: 'Company', render: (r) => r.a.domain },
            { key: 'a', label: 'First', render: (r) => `${r.a.title ?? '—'} · @${r.a.email_domain} · ${r.a.source}` },
            { key: 'b', label: 'Second', render: (r) => `${r.b.title ?? '—'} · @${r.b.email_domain} · ${r.b.source}` },
            { key: 'id', label: '', render: (r) => <ActionButton path="/v1/contacts/possible-duplicates/dismiss" body={{ a_id: r.a.id, b_id: r.b.id }} label="Different people" /> },
          ]} />
        </Section>
      ) : null}</Load>
      <Link to="/import">Back to imports</Link>
    </Page>
  );
}

type PossibleCompany = { id: string; domain: string; name: string | null; relationship: string; status: string | null; contacts: number };
type PossiblePair = { reason: 'same_name' | 'same_domain_name'; a: PossibleCompany; b: PossibleCompany; suggested_survivor_id: string };
type PossiblePeople = { a: { id: string; first_name: string | null; last_name: string | null; title: string | null; source: string; email_domain: string; domain: string }; b: PossiblePeople['a'] };

/** A suggested pair: merge into the one to keep (a person confirms), or keep them apart. */
function PossiblePairView({ pair }: { pair: PossiblePair }) {
  const keep = pair.suggested_survivor_id === pair.a.id ? pair.a : pair.b;
  const other = keep === pair.a ? pair.b : pair.a;
  const line = (c: PossibleCompany) => `${c.domain}${c.name ? ` (${c.name})` : ''} — ${c.relationship.replace(/_/g, ' ')}, ${c.status ?? 'not researched'}, ${c.contacts} contacts`;
  return (
    <Section title={`${pair.a.name ?? pair.a.domain} · ${pair.reason === 'same_name' ? 'same name' : 'same name before the suffix'}`}>
      <ul className="small"><li>Keep: {line(keep)}</li><li>Merge: {line(other)}</li></ul>
      <span className="actions">
        <ActionButton path={`/v1/companies/${keep.id}/merge`} body={{ merge_ids: [other.id], confirm_different_domains: true }} label={`Merge ${other.domain} into ${keep.domain}`}
          confirm={`Merge ${other.domain} into ${keep.domain}? Its contacts, research, deals and actions move, and ${other.domain} lands on ${keep.domain} in later imports. It cannot be undone.`} variant="primary" />
        <ActionButton path="/v1/companies/possible-duplicates/dismiss" body={{ a_id: pair.a.id, b_id: pair.b.id }} label="Not the same" />
      </span>
    </Section>
  );
}

function DuplicateGroupView({ group }: { group: DupGroup }) {
  const [keep, setKeep] = useState(group.suggested_survivor_id);
  const others = group.companies.filter((c) => c.id !== keep).map((c) => c.id);
  const name = `keep-${group.registrable}`;
  return (
    <Section title={group.registrable}>
      <fieldset className="form">
        <legend className="small">Keep</legend>
        {group.companies.map((c) => (
          <div className="field field-check" key={c.id}>
            <input type="radio" id={`${name}-${c.id}`} name={name} value={c.id} checked={keep === c.id} onChange={() => setKeep(c.id)} />
            <label htmlFor={`${name}-${c.id}`}>{c.domain}{c.name ? ` (${c.name})` : ''} — {c.relationship.replace(/_/g, ' ')}, {c.status ?? 'not researched'}; {c.contacts} contacts, {c.deals} deals, {c.actions} actions</label>
          </div>
        ))}
      </fieldset>
      <ActionButton path={`/v1/companies/${keep}/merge`} body={{ merge_ids: others }} label={`Merge ${others.length} into ${group.companies.find((c) => c.id === keep)?.domain}`}
        confirm="Merge them? Their records move to the company you keep." variant="primary" />
    </Section>
  );
}
